import React, { FC, useMemo } from 'react'
import styled from '@emotion/styled'
import Link from 'next/link'
import { Colors, printStyle, Shapes } from 'src/components/theme'
import { Close } from '@emotion-icons/material/Close'
import { Typography } from '@mui/material'
import { useParsedRouter } from 'src/layout/hooks/use-parsed-router'
import { EmotionIcon } from '@emotion-icons/emotion-icon'
import { rgbaStrOpacity } from 'src/utils/colors'
import { FlexRow } from 'src/components/flex-containers'

export const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  padding: 20px ${Shapes.spacing[5]}px;
  border-bottom: 1px solid ${Colors.neutral100};
`

export const LeftContent = styled.div`
  display: flex;
`

type IconProps = {
  color: string
  bgColor: string
}

export const IconBox = styled.div<IconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${Shapes.insightsPageTopIconBoxSize}px;
  height: ${Shapes.insightsPageTopIconBoxSize}px;
  padding: ${Shapes.spacing[3]}px;
  border-radius: 6px;
  border: 1px solid ${(props) => props.color};
  margin-right: ${Shapes.spacing[4]}px;
  background-color: ${(props) => props.bgColor};
`

export const TitleWrapper = styled.div`
  height: ${Shapes.insightsPageTopIconBoxSize}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${Shapes.spacing[1]}px;
`

const RightContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${Shapes.spacing[2]}px;
`

const CloseButton = styled(Close)`
  width: 20px;
  height: 20px;
  fill: ${Colors.textBlack};
  cursor: pointer;

  &:hover {
    fill: ${Colors.textDark};
  }
  ${printStyle`
  display: none;
  `}
`

type InsightToolbarProps = {
  title: string
  icon: EmotionIcon | React.ElementType
  subtitle?: React.ReactNode
  color?: string
  iconBgColor?: string
  actions?: React.ReactNode
  iconSize?: number
  titleIcon?: React.ReactNode
}

export const InsightToolbar: FC<InsightToolbarProps> = ({
  title,
  icon: Icon,
  subtitle,
  color = Colors.primaryRed700,
  iconBgColor,
  actions,
  iconSize,
  titleIcon,
}) => {
  const { router, query } = useParsedRouter()

  const closeRedir = useMemo(() => {
    if (router.pathname.includes('owner-reveal')) {
      return {
        pathname: '/owner-reveal',
        query: query,
      }
    }

    return {
      pathname: '/',
      query: query,
    }
  }, [router, query])

  return (
    <Container>
      <LeftContent>
        <IconBox color={color} bgColor={iconBgColor ?? rgbaStrOpacity(color, 0.1)}>
          <Icon color={color} size={iconSize} />
        </IconBox>
        <TitleWrapper>
          <FlexRow gap={Shapes.spacing[2]}>
            <Typography variant="title1" color={Colors.textBlack}>
              {title}
            </Typography>
            {titleIcon}
          </FlexRow>
          <Typography variant="textmdm" color={Colors.textMedium}>
            {subtitle}
          </Typography>
        </TitleWrapper>
      </LeftContent>
      <RightContent>
        {actions}
        <Link href={closeRedir}>
          <CloseButton />
        </Link>
      </RightContent>
    </Container>
  )
}
